import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Role, CreateRoleForm, UpdateRoleForm } from '../../types';

const schema = yup.object({
  name: yup
    .string()
    .required('Role name is required')
    .min(2, 'Role name must be at least 2 characters')
    .max(50, 'Role name must not exceed 50 characters'),
  description: yup
    .string()
    .max(200, 'Description must not exceed 200 characters')
});

interface RoleFormProps {
  role?: Role | null;
  onSubmit: (data: CreateRoleForm | UpdateRoleForm) => void;
  isLoading?: boolean;
}

const RoleForm: React.FC<RoleFormProps> = ({ role, onSubmit, isLoading = false }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<CreateRoleForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: role?.name || '',
      description: role?.description || ''
    },
    mode: 'onChange'
  });

  const handleFormSubmit = (data: CreateRoleForm) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Role Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Role Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          {...register('name')}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 ${
            errors.name ? 'border-red-300 dark:border-red-500' : ''
          }`}
          placeholder="Enter role name"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          id="description"
          {...register('description')}
          rows={3}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 ${
            errors.description ? 'border-red-300 dark:border-red-500' : ''
          }`}
          placeholder="Enter role description (optional)"
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description.message}</p>
        )}
      </div>

      {/* User Count Info (for edit mode) */}
      {role && role.userCount !== undefined && (
        <div className="bg-accent-color-50 dark:bg-accent-color-900 border border-accent-color-200 dark:border-accent-color-700 rounded-md p-3">
          <p className="text-sm text-accent-color-800 dark:text-accent-color-200">
            <strong>{role.userCount}</strong> user{role.userCount !== 1 ? 's' : ''} currently assigned to this role.
          </p>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-color"
          onClick={() => window.history.back()}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!isValid || isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-accent-color border border-transparent rounded-md hover:bg-accent-color-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-color disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : (role ? 'Update Role' : 'Create Role')}
        </button>
      </div>
    </form>
  );
};

export default RoleForm;
